import { useId } from "react";
import { cn } from "@/lib/utils";

type SparklineProps = {
  points: readonly (number | null)[];
  width?: number;
  height?: number;
  color?: string;
  className?: string;
};

/**
 * Minimal dependency-free SVG sparkline used by the ranking strip. Draws a
 * filled area + stroke line through the non-null values and highlights the
 * most recent point.
 */
export function Sparkline({ points, width = 68, height = 26, color = "#1e66f5", className }: SparklineProps) {
  const rawGradientId = useId();
  const gradientId = rawGradientId.replaceAll(":", "");

  const values = points.filter((value): value is number => value !== null && Number.isFinite(value));
  if (values.length === 0) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const padX = 2;
  const padY = 3;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const step = values.length > 1 ? innerW / (values.length - 1) : 0;

  const coords = values.map((value, index) => {
    const x = padX + index * step;
    const y = padY + innerH - ((value - min) / span) * innerH;
    return [x, y] as const;
  });

  const line = coords.map(([x, y], index) => `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const last = coords.at(-1) ?? [padX, height / 2];
  const area = `${line} L${last[0].toFixed(1)},${height - 1} L${coords[0][0].toFixed(1)},${height - 1} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width='100%'
      height={height}
      aria-hidden='true'
      className={cn("block overflow-visible", className)}>
      <defs>
        <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
          <stop offset='0%' stopColor={color} stopOpacity='0.28' />
          <stop offset='100%' stopColor={color} stopOpacity='0.02' />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} />
      <path d={line} fill='none' stroke={color} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
      <circle cx={last[0]} cy={last[1]} r='2.2' fill={color} />
    </svg>
  );
}
